import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const portGrimaud: ChauffeurPage = {
  slug: "port-grimaud",
  title: "Chauffeur privé à Port Grimaud – VTC cité lacustre",
  subtitle:
    "Chauffeur privé et VTC à Port Grimaud : transfert aéroport de Nice, gare de Saint-Raphaël, mise à disposition et liaison Saint-Tropez, au bord du golfe.",
  keywords: [
    "chauffeur privé port grimaud",
    "vtc port grimaud",
    "chauffeur port grimaud",
    "transfert port grimaud aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Port Grimaud",
    subtitle:
      "La cité lacustre aux canaux, face au golfe : transfert aéroport de Nice, mise à disposition et longue distance, 7j/7.",
    keywords: [
      "VTC Port Grimaud",
      "chauffeur cité lacustre",
      "transfert aéroport Nice",
      "chauffeur golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Chauffeur privé & VTC à Port Grimaud (cité lacustre, golfe de Saint-Tropez)",
    badges: [
      "Cité lacustre & canaux",
      "Transfert aéroport de Nice",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Port Grimaud",
    items: [
      {
        title: "Entrée de la cité maîtrisée",
        description:
          "Le cœur lacustre est piéton, desservi par les canaux et le coche d’eau : le chauffeur dépose et récupère aux parkings d’entrée, sans tourner.",
      },
      {
        title: "Au bord du golfe",
        description:
          "Amarrages, quais et plages à deux pas de Saint-Tropez : une desserte fluide de tout le golfe.",
      },
      {
        title: "Transfert longue distance",
        description:
          "Depuis l’aéroport de Nice, environ 1h20 à 1h30 de route, gérées dans un confort haut de gamme.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Port Grimaud",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Port Grimaud ⇄ aéroport de Nice Côte d’Azur avec suivi de vol ; trajet plus long qu’au départ de Cannes, calé dès la prise en charge.",
      },
      {
        title: "Gare TGV de Saint-Raphaël",
        text: "Prise en charge à la gare de Saint-Raphaël, à environ 30 à 40 minutes, comme alternative au train.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée pour les canaux, les amarrages, les plages et Saint-Tropez.",
      },
      {
        title: "Liaison Saint-Tropez & port",
        text: "Port Grimaud ⇄ Saint-Tropez et son port, restaurants et soirées, avec retour de nuit.",
      },
      {
        title: "Équipages & propriétaires",
        text: "Transfert des équipages, invités et propriétaires depuis les amarrages, en coordination avec la conciergerie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Cité lacustre",
        description: "Maisons de pêcheurs colorées bordant les canaux.",
      },
      {
        name: "Église Saint-François-d’Assise",
        description:
          "Église moderne au cœur de la cité, vue depuis son campanile.",
      },
      {
        name: "Coche d’eau & pontons",
        description: "Navette fluviale et amarrages privatifs entre les quais.",
      },
      {
        name: "Plage de Port Grimaud",
        description: "Plage surveillée à l’entrée du golfe.",
      },
      {
        name: "Gare de Saint-Raphaël",
        description: "Gare TGV à environ 30-40 min, point de transfert.",
      },
      {
        name: "Aéroport de Nice",
        description: "Aéroport Nice Côte d’Azur, à environ 1h20-1h30.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour Port Grimaud",
    subheading:
      "De l’entrée de la cité lacustre aux amarrages, un service premium au bord du golfe de Saint-Tropez.",
    paragraphs: [
      "Port Grimaud, la « Venise provençale », s’organise autour de ses canaux : maisons colorées, pontons privatifs et coche d’eau desservent un cœur presque entièrement piéton. On y arrive par les parkings d’entrée, puis à pied ou par l’eau ; un chauffeur qui dépose et récupère au bon accès évite bien des détours avec les bagages.",
      "Comme tout le golfe, Port Grimaud n’a pas d’aéroport : l’arrivée se fait le plus souvent par Nice Côte d’Azur, à environ 1h20 à 1h30 de route. Nous ajustons l’heure de départ selon le trafic estival, et proposons la gare TGV de Saint-Raphaël, à une trentaine de minutes, comme point de transfert alternatif.",
      "Pour la clientèle des amarrages et des villas, un même chauffeur relie les quais, les plages, Grimaud et Saint-Tropez tout proche, avec discrétion et horaires souples. Forfaits fixes définis à l’avance ou mise à disposition à l’heure : devis immédiat.",
    ],
    bullets: [
      "Dépose aux accès de la cité",
      "Transfert aéroport de Nice & gare de Saint-Raphaël",
      "Coordination amarrages & villas",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Port Grimaud",
    steps: [
      "Indiquez votre accès (parking d’entrée, amarrage, quai) et vos horaires.",
      "Nous recommandons le véhicule et le forfait adaptés.",
      "Le chauffeur suit votre vol ou votre train et vous accueille au bon point.",
      "Transfert direct ou mise à disposition pour le golfe pendant votre séjour.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Port Grimaud"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Environ 1h20 à 1h30 hors affluence, davantage en été et lors des pics de trafic du golfe. Nous adaptons le départ et suivons votre vol.",
    },
    {
      q: "Desservez-vous la gare TGV de Saint-Raphaël ?",
      a: "Oui, la gare de Saint-Raphaël est à environ 30 à 40 minutes de Port Grimaud ; un point de transfert alternatif pratique pour le train.",
    },
    {
      q: "Assurez-vous la liaison avec Saint-Tropez ?",
      a: "Oui, Saint-Tropez est tout proche ; nous assurons les allers-retours vers le port, les restaurants et les soirées, avec retour de nuit.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "En haute saison estivale, réservez au plus tôt : la demande est forte sur le golfe. Nous prenons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués aux séjours en villa et à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes, van premium (Classe V), SUV et minibus selon les passagers et les bagages, adaptés à la longue distance depuis Nice ou Cannes.",
    },
    {
      q: "Peut-on entrer en voiture dans la cité lacustre ?",
      a: "Le cœur de Port Grimaud est piéton et bordé de canaux. Le chauffeur vous dépose au parking d’entrée ou à l’accès le plus proche de votre amarrage, puis vous récupère au même point.",
    },
  ],
  relatedLinks: [
    {
      label: "VTC à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    { label: "Voiture avec chauffeur à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Voiture avec chauffeur sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Voitures à louer à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Port Grimaud",
    text: "Transfert aéroport de Nice, gare de Saint-Raphaël ou soirée à Saint-Tropez : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Port Grimaud — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Port Grimaud : transfert aéroport de Nice, gare de Saint-Raphaël, mise à disposition et liaison Saint-Tropez.",
    canonical: "/chauffeur/port-grimaud",
  },
};

export default portGrimaud;
